import { isEqual } from 'lodash';
import { aiProcessDocumentTagsSchema, aiProcessDocumentTagSchema, PROCESS_SOURCE } from './schema';
import { AI_DOCUMENT_TAG_OPTIONS } from './constants';
import {
  documentFileId,
  documentFileName,
  documentRowKey,
  documentSizeBytes,
  formatFileIdForDisplay
} from '../../utils/documentUtils';
import { buildChangedPatch } from '../../utils/patchUtils';

export {
  documentFileId as sourceDocumentFileId,
  documentFileName as sourceDocumentName,
  documentRowKey as processDocumentRowKey,
  documentSizeBytes as processDocumentSizeBytes,
  formatFileIdForDisplay,
  formatFileSize
} from '../../utils/documentUtils';

export function toTagList(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

export function sourceLabel(source) {
  const normalized = String(source || '').toLowerCase();
  if (normalized === PROCESS_SOURCE.AI_ASSISTED) return 'AI Assisted';
  if (normalized === PROCESS_SOURCE.BATCH_IMPORT) return 'Batch Import';
  return 'Manual';
}

/** Business process code for edit URLs (e.g. `otc-p3` from `processes[].process_id`). */
export function processIdForEditRoute(process) {
  return String(process?.process_id ?? process?.id ?? '').trim();
}

/** Primary UUID for DELETE / mutations that require `processes[].id`. */
export function processUuidForApi(process) {
  return String(process?.id ?? process?.process_uuid ?? process?.process_id ?? '').trim();
}

/** AI/review drafts use client-only ids; never call DELETE /api/processes for these. */
export function isLocalDraftProcessId(id) {
  const s = String(id ?? '');
  return s.startsWith('temp-') || s.startsWith('temp-ai-');
}

/** React Query key for unsaved AI/bulk draft rows (must not drive the processes list). */
export function processDraftQueryKey(cycleId) {
  return ['draft-processes', String(cycleId ?? '').trim()];
}

/** Drop cached draft extraction/upload rows when the user cancels or leaves without saving. */
export function clearProcessDraftCache(queryClient, cycleId) {
  const id = String(cycleId ?? '').trim();
  if (!id || !queryClient) return;
  queryClient.removeQueries({ queryKey: processDraftQueryKey(id) });
}

export function formatProcessNameForList(name) {
  const s = String(name ?? '');
  if (s.length <= 80) return s;
  return `${s.slice(0, 80)}…`;
}

export function getFileExtension(fileName) {
  return String(fileName || '').split('.').pop()?.toLowerCase() || '';
}

export function buildTagOption(tag) {
  return { value: tag, label: tag };
}

export function getAiDocumentTagLabel(value) {
  return AI_DOCUMENT_TAG_OPTIONS.find((option) => option.value === value)?.label || value;
}

export function getAiDocumentTagValidationMessage(existingTags = [], rawValue = '') {
  const value = String(rawValue || '');
  if (!value.trim()) return '';
  try {
    aiProcessDocumentTagSchema.validateSync(value);
    aiProcessDocumentTagsSchema.validateSync([...(existingTags || []), value]);
    return '';
  } catch (error) {
    return error?.message || 'Invalid tag';
  }
}

/** Raw file rows from GET /api/processes/{id} (`files`, legacy `documents`). */
export function getProcessFilesFromApi(process) {
  if (Array.isArray(process?.files)) return process.files;
  if (Array.isArray(process?.documents)) return process.documents;
  return [];
}

/** Normalize API `files[]` rows for the document library UI. */
export function normalizeProcessFileRow(file) {
  const fileId = documentFileId(file);
  const fileName = documentFileName(file, fileId);
  return {
    file_id: fileId || null,
    file_name: fileName,
    filename: fileName,
    name: fileName,
    tags: Array.isArray(file?.tags) ? file.tags : [],
    size_bytes: documentSizeBytes(file)
  };
}

/** Linked process files for the document library (from `files` on process details). */
export function getProcessDocumentsFromApi(process) {
  return getProcessFilesFromApi(process).map(normalizeProcessFileRow);
}

function isPendingProcessDocumentRow(document) {
  return Boolean(document?.file) && !documentFileId(document);
}

/**
 * Merge refetched API documents with in-modal / in-form local rows.
 * Keeps pending uploads and freshly linked files until the process GET includes them.
 */
export function mergeLocalProcessDocumentsWithApi(localDocuments, apiDocuments) {
  const local = Array.isArray(localDocuments) ? localDocuments : [];
  const api = Array.isArray(apiDocuments) ? apiDocuments : [];
  const apiFileIds = new Set(api.map((document) => documentFileId(document)).filter(Boolean));

  const preserveLocal = local.filter((document) => {
    if (isPendingProcessDocumentRow(document)) return true;
    if (document?.isUploading) return true;
    const fileId = documentFileId(document);
    return Boolean(fileId && !apiFileIds.has(fileId));
  });

  const merged = [...api];
  preserveLocal.forEach((document) => {
    const rowKey = documentRowKey(document);
    if (!merged.some((row) => documentRowKey(row) === rowKey)) {
      merged.push(document);
    }
  });
  return merged;
}

function cloneSourceDocumentsList(documents) {
  return (Array.isArray(documents) ? documents : []).map((document) => ({
    file_id: document?.file_id ?? null,
    file_name: document?.file_name ?? document?.filename ?? document?.name ?? null,
    tags: Array.isArray(document?.tags) ? [...document.tags] : []
  }));
}

function createAiDraftRowId(index) {
  const fallback = `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 9)}`;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `temp-ai-${crypto.randomUUID()}`;
  }
  return `temp-ai-${fallback}`;
}

/**
 * Assign stable unique `id` values and per-row copies of shared extraction metadata.
 * Extract API rows often omit `id` or repeat the same value — without this, edits and source
 * file picks apply to every row in the review table.
 */
export function normalizeAiExtractedProcessRows(processes, metadata = {}) {
  const list = Array.isArray(processes) ? processes : [];
  const defaultSourceDocuments = cloneSourceDocumentsList(metadata?.documents);
  const defaultFileIds = (Array.isArray(metadata?.file_ids) ? metadata.file_ids : [])
    .map((id) => String(id ?? '').trim())
    .filter(Boolean);

  const usedIds = new Set();

  return list.map((process, index) => {
    const candidate = String(process?.id ?? process?.process_id ?? '').trim();
    let id = candidate;
    if (!id || usedIds.has(id)) {
      id = createAiDraftRowId(index);
    }
    usedIds.add(id);

    const rowSourceDocs = process?.sourceDocuments
      ? cloneSourceDocumentsList(process.sourceDocuments)
      : cloneSourceDocumentsList(defaultSourceDocuments);

    const rowFileIds = Array.isArray(process?.file_ids)
      ? process.file_ids.map((fid) => String(fid ?? '').trim()).filter(Boolean)
      : [...defaultFileIds];

    const rawSequence = process?.sequence ?? process?.order;
    const parsedSequence = Number(rawSequence);
    const sequence = Number.isInteger(parsedSequence) && parsedSequence >= 1 ? parsedSequence : index + 1;

    return {
      ...process,
      id,
      name: process?.name ?? '',
      description: process?.description ?? '',
      owner: process?.owner ?? '',
      status: process?.status ?? 'open',
      file_ids: rowFileIds,
      sourceDocuments: rowSourceDocs,
      sequence
    };
  });
}

/**
 * Documents + file metadata from a process API payload (library, PATCH, flowchart).
 * @param {object | null | undefined} process
 */
export function resolveProcessDocumentState(process) {
  const documents = getProcessDocumentsFromApi(process);
  const linkedFileIds = extractProcessFileIds(documents);
  return {
    documents,
    linkedFileIds,
    hasLinkedFiles: linkedFileIds.length > 0
  };
}

/** Whether the process detail payload includes any linked files. */
export function hasProcessFilesFromApi(process) {
  return resolveProcessDocumentState(process).hasLinkedFiles;
}

/** Version number from POST /api/generateFlowchart response (shape may vary). */
export function parseGeneratedFlowchartVersion(data) {
  if (data == null || typeof data !== 'object') return null;
  const raw =
    data.versionNumber ??
    data.flowchartVersionNumber ??
    data.version_number ??
    data.flowchart_version_number ??
    data.flowchart?.versionNumber ??
    data.flowchart?.version_number;
  if (raw == null || raw === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/** First N linked file ids for default flowchart selection (selection is not persisted on PATCH). */
export function initialFlowchartSelectedFileIds(documents, cap) {
  const limit = Number(cap);
  if (!Number.isFinite(limit) || limit <= 0) return new Set();
  const ids = (Array.isArray(documents) ? documents : [])
    .map((document) => documentFileId(document))
    .filter(Boolean);
  return new Set(ids.slice(0, limit));
}

export function appendFlowchartSelectedFileIds(previousSet, fileIdsToAdd, cap) {
  const limit = Number(cap);
  if (!Number.isFinite(limit) || limit <= 0) return new Set();
  const next = new Set(previousSet instanceof Set ? previousSet : []);
  for (const rawId of fileIdsToAdd) {
    const id = String(rawId ?? '').trim();
    if (!id) continue;
    if (next.size >= limit && !next.has(id)) continue;
    next.add(id);
  }
  return new Set([...next].slice(0, limit));
}

export function fileIdsForUploadedPendingDocuments(pendingDocs, documentsAfterUpload) {
  return (Array.isArray(pendingDocs) ? pendingDocs : [])
    .map((pending) => {
      const key = documentRowKey(pending);
      const match = (Array.isArray(documentsAfterUpload) ? documentsAfterUpload : []).find(
        (document) => documentRowKey(document) === key
      );
      return documentFileId(match);
    })
    .filter(Boolean);
}

/**
 * After upload, merge new file ids into flowchart selection (respects cap).
 * @param {Set<string>} previousSet
 * @param {Array<object>} pendingDocs
 * @param {Array<object>} documentsAfterUpload
 * @param {number} cap
 */
export function mergeFlowchartSelectionAfterUpload(previousSet, pendingDocs, documentsAfterUpload, cap) {
  const uploadedIds = fileIdsForUploadedPendingDocuments(pendingDocs, documentsAfterUpload);
  const previousSize = previousSet instanceof Set ? previousSet.size : 0;
  const nextSet = appendFlowchartSelectedFileIds(previousSet, uploadedIds, cap);
  return {
    nextSet,
    uploadedIds,
    selectionLimitReached: uploadedIds.length > 0 && nextSet.size - previousSize < uploadedIds.length
  };
}

export function pendingProcessDocumentSignatures(documents) {
  return (Array.isArray(documents) ? documents : [])
    .filter((document) => !documentFileId(document) && document?.file)
    .map(
      (document) =>
        `${documentRowKey(document)}:${documentFileName(document)}:${documentSizeBytes(document)}`
    )
    .sort()
    .join('|');
}

export function processDocumentsChanged(previousDocuments, currentDocuments) {
  if (!isEqual(extractProcessFileIds(previousDocuments), extractProcessFileIds(currentDocuments))) {
    return true;
  }
  return (
    pendingProcessDocumentSignatures(previousDocuments) !==
    pendingProcessDocumentSignatures(currentDocuments)
  );
}

/** Sorted `file_ids` for PATCH / change detection (linked files only). */
export function extractProcessFileIds(documents) {
  return (Array.isArray(documents) ? documents : [])
    .map((document) => documentFileId(document))
    .filter(Boolean)
    .sort();
}

/** @deprecated Use {@link extractProcessFileIds} — PATCH uses `file_ids` only. */
export function toProcessDocumentsPatchPayload(documents) {
  return extractProcessFileIds(documents);
}

export function normalizeProcessFormForPatch(values) {
  const toArray = (value) =>
    String(value ?? '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  return {
    name: String(values?.name ?? '').trim(),
    description: String(values?.description ?? '').trim(),
    owner: String(values?.owner ?? '').trim(),
    actors: toArray(values?.actors),
    systems: toArray(values?.systems)
  };
}

export function buildProcessEditPatch(
  previousForm,
  currentForm,
  previousDocuments,
  currentDocuments,
  source
) {
  const patch = buildChangedPatch(previousForm, currentForm, {
    scalarKeys: ['name', 'description', 'owner'],
    arrayKeys: ['actors', 'systems']
  });

  const nextFileIds = extractProcessFileIds(currentDocuments);
  if (processDocumentsChanged(previousDocuments, currentDocuments)) {
    patch.file_ids = nextFileIds;
  }

  if (Object.keys(patch).length > 0) {
    const normalizedSource = String(source ?? PROCESS_SOURCE.MANUAL).trim().toLowerCase();
    if (normalizedSource) {
      patch.source = normalizedSource;
    }
  }

  return patch;
}

export function hasProcessEditChanges(
  previousForm,
  currentForm,
  previousDocuments,
  currentDocuments,
  source
) {
  return Object.keys(
    buildProcessEditPatch(previousForm, currentForm, previousDocuments, currentDocuments, source)
  ).length > 0;
}

/**
 * @param {Array<{ file_id?: unknown; file_name?: unknown }>} [sourceDocuments]
 * @param {Array<string>} [selectedFileIds]
 * @returns {Array<{ value: string; label: string }>}
 */
export function buildSourceFileOptions(sourceDocuments = [], selectedFileIds = []) {
  const fromDocuments = (Array.isArray(sourceDocuments) ? sourceDocuments : []).flatMap((document) => {
    const fileId = documentFileId(document);
    return fileId ? [{ value: fileId, label: documentFileName(document, fileId) }] : [];
  });

  const fromSelected = (Array.isArray(selectedFileIds) ? selectedFileIds : [])
    .map((fileId) => String(fileId ?? '').trim())
    .filter(Boolean)
    .map((fileId) => ({ value: fileId, label: formatFileIdForDisplay(fileId) }));

  const seen = new Set();
  return [...fromDocuments, ...fromSelected].filter((option) => {
    if (seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
}
